<template>
  <transition name="fade" mode="out-in">
    <div id="prereg" key="prereg" class="application-section">
      <section id="registrationNumberSection" :class="sections.registrationNumberSection">
          <div class="container is-fluid">
            <FreetextQuestion question="My GPhC Foundation Trainee number is" closingTag="."
                              :existingValue="understandingYou.regNumber" :tab="sections.registrationNumberSection.tab"
                              :isDisabled="sections.registrationNumberSection.ghost" :placeholder="placeholderNumber"
                              @question-answered="registrationNumberEntered" :class="{error: $v.understandingYou.regNumber.$error}"
                              :isMandatory="true" :isDropdownControlled="true" :isNotDisplayed="true" />
            <ChooseQuestion question="I am intending to take my registration assessment in" :isDisabled="sections.registrationNumberSection.ghost"
                            :existingValue="assessmentDate" :tab="sections.registrationNumberSection.tab"
                            :options="traineeAssessments" @question-answered="assessmentTimeSelected" closingSentence="."
                            :class="{error: $v.understandingYou.assessment.$error}" :isMandatory="true" />
          </div>
       </section>
      <section id="universityTrainingSection" :class="sections.universityTrainingSection">
        <div class="container is-fluid">
          <ChooseQuestion question="I studied for my degree at" :options="universities"
                          :isDisabled="sections.universityTrainingSection.ghost" @question-answered="universitySelected"
                          closingSentence="." :class="{error: $v.understandingYou.university.$error}" :isMandatory="true"
                          :isDropdownControlled="true" :existingValue="understandingYou.university"
                          :tab="sections.universityTrainingSection.tab"/>
          <FreetextQuestion question="Please state the name of the institution you are studying:" :placeholder="placeholder"
                            v-if="isUniExtraQuestion" @question-answered="otherInstitutionEntered"
                            :class="{error: $v.understandingYou.universityOther.$error}" :isMandatory="true" :isDropdownControlled="true"
                            :isExtraQuestion="true" :isNotDisplayed="true" :existingValue="understandingYou.universityOther"
                            :tab="sections.universityTrainingSection.tab" maxlength="255"/>
          <AutocompleteQuestion question="Please state the country in which you are studying:" closingSentence="."
                                :placeholder="placeholder" v-if="isUniExtraQuestion" @question-answered="studiedCountryEntered"
                                :class="{error: $v.understandingYou.studiedCountry.$error}" :isMandatory="true"
                                :isExtraQuestion="true" :isNotDisplayed="true" :existingValue="understandingYou.studiedCountry"
                                :tab="sections.universityTrainingSection.tab" :dataSource="countries" />
        </div>
      </section>
      <section id="primarySectorSection" :class="sections.primarySectorSection">
        <div class="container is-fluid">
          <ChooseQuestion question="My primary sector of pharmacy is" :options="sectors"
                          :isDisabled="sections.primarySectorSection.ghost" @question-answered="sectorSelected" closingSentence="."
                          :isDropdownControlled="true" :existingValue="understandingYou.primarySector"
                          :tab="sections.primarySectorSection.tab" />
          <FreetextQuestion question="I work as a Foundation Trainee at" closingSentence="."
                          :isDisabled="sections.primarySectorSection.ghost" @question-answered="organizationEntered"
                          :isNotDisplayed="true" :existingValue="understandingYou.organization"
                          :tab="sections.primarySectorSection.tab" maxlength="255"/>
          <div v-if="!this.$store.state.isMobile" id="confirm-wrap">
            <button class="btn-confirm" @click.stop.prevent="$emit('confirmClicked')" type="button">Next</button>
          </div>
        </div>
      </section>
      </div>
  </transition>
</template>
<script>
/* eslint-disable no-unused-expressions */
import { mapGetters } from 'vuex';
import FreetextQuestion from '@/components/FreetextQuestion.vue';
import ChooseQuestion from '@/components/ChooseQuestion.vue';
import AutocompleteQuestion from '@/components/AutocompleteQuestion.vue';
import Manager from '@/applicationManager';
import '@/styles/application.scss';
import { required, requiredIf } from 'vuelidate/lib/validators';
import ghost from '@/understandingyou/scripts/ghost';

export default {
  name: 'PreReg',
  components: {
    FreetextQuestion,
    ChooseQuestion,
    AutocompleteQuestion,
  },
  props: {
    dataGetter: Function,
  },
  async mounted() {
    this.countries = await Manager.getCountryList();
  },
  updated() {
    this.$emit('onFinalSection', this.isFinalSectionVisible);
  },
  data() {
    return {
      placeholderNumber: '000000',
      isUniExtraQuestion: false,
      placeholder: 'Tell us more',
      sections: {
        registrationNumberSection: { hidden: false, ghost: false, tab: 0 },
        universityTrainingSection: { hidden: false, ghost: true, tab: this.$store.state.isMobile ? -1 : 0 },
        primarySectorSection: { hidden: true, ghost: false, tab: this.$store.state.isMobile ? -1 : 0 },
      },
      understandingYou: this.dataGetter(),
      countries: [],
    };
  },
  computed: {
    ...mapGetters(['traineeAssessments', 'universities', 'sectors', 'mapExistingPreRegExamDate']),
    assessmentDate() {
      return this.mapExistingPreRegExamDate(this.understandingYou.assessment);
    },
    universityIsOther() {
      return this.understandingYou.university === 'Other';
    },
    isFinalSectionVisible() {
      return this.sections.primarySectorSection.hidden === false;
    },
    isMobile() {
      return this.$store.state.isMobile;
    },
  },
  validations() {
    return {
      understandingYou: {
        university: {
          required,
        },
        universityOther: {
          required: requiredIf(() => this.universityIsOther),
        },
        studiedCountry: {
          required: requiredIf(() => this.universityIsOther),
        },
        regNumber: {
          required,
        },
        assessment: {
          required,
        },
      },
    };
  },
  methods: {
    HasErrorInSection(section) {
      const { understandingYou: prereg } = this.$v;
      const sectionValidations = {
        registrationNumberSection() {
          prereg.regNumber.$touch();
          prereg.assessment.$touch();
          return prereg.regNumber.$error || prereg.assessment.$error;
        },
        universityTrainingSection() {
          prereg.university.$touch();
          prereg.universityOther.$touch();
          prereg.studiedCountry.$touch();
          return prereg.university.$error || prereg.universityOther.$error || prereg.studiedCountry.$error;
        },
      };

      const validation = sectionValidations[section];

      if (validation) {
        return validation();
      }

      return false;
    },
    universitySelected(u) {
      u.text === 'Other' ? this.isUniExtraQuestion = true : this.isUniExtraQuestion = false;

      this.understandingYou.university = u.text;
      ghost.showSection(this.sections.primarySectorSection);
    },
    registrationNumberEntered(r) {
      this.understandingYou.regNumber = r;
      this.$v.understandingYou.regNumber.$touch();
    },
    assessmentTimeSelected(assessmentYear) {
      this.understandingYou.assessment = assessmentYear.value;
      ghost.showSection(this.sections.universityTrainingSection);
      ghost.ghostSection(this.sections.primarySectorSection);
    },
    otherInstitutionEntered(i) {
      this.understandingYou.universityOther = i;
      this.$v.understandingYou.universityOther.$touch();
    },
    studiedCountryEntered(c) {
      this.understandingYou.studiedCountry = c;
      this.$v.understandingYou.studiedCountry.$touch();
    },
    sectorSelected(s) {
      this.understandingYou.primarySector = s.text;
    },
    organizationEntered(o) {
      this.understandingYou.organization = o;
    },
    SetIndexes(current, next) {
      this.sections[current].tab = -1;
      this.sections[next].tab = 0;
    },
  },
};
</script>
<style lang="scss">
  @import '../../styles/mixins';
  /*call modal builtin classes stop increase font-face size*/
  .modal-card .modal-card-foot .button {
    font-size: 11.5px !important;
  }

  .modal-card-title {
    font-size: 18px !important;
  }
  /*use poppin for member registered text*/
  #isRegisteredSection div.registered-text p.registered {
    @include DefaultPoppinFont();
  }
</style>
